import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import {
  createSystemMessage,
  createUserMessage,
  diagramToJson,
  getWorkflowBackgroundSections,
  getEventsDataModelsBackgroundSections
} from '@/ai/utils/promptUtils'

export const buildEntitiesFromWorkflowPrompt = ({ workflow, svgGraph, svgLanes, cardTypes, stream }) => {
  const messages = []

  messages.push(createSystemMessage(systemMessages.EVENT_MODELING_DDD_EXPERT))

  const promptSections = []

  const { workflow: currentDiagram } = workflow.eventsJson?.length
    ? diagramToJson(null, workflow, svgGraph, svgLanes)
    : { workflow: [], currentStep: undefined }
  const workflowBackgroundSections = getWorkflowBackgroundSections(workflow, currentDiagram, cardTypes)
  const dataModelBackgroundSections = getEventsDataModelsBackgroundSections(workflow.eventsJson, currentDiagram, cardTypes)

  promptSections.push(...workflowBackgroundSections)

  promptSections.push(...dataModelBackgroundSections)

  promptSections.push({
    title: 'Request',
    content: `
      Review the information provided above and generate all the entities involved in the execution
      of the commands - the aggregate root and the related entities. One entity is typically the aggregate
      root (in DDD terminology) and the other entities are associated entities that are referenced by the aggregate
      (not necessarily part of the aggregate). The number of attributes/fields per entity depends on the write
      and read models and can vary, but aim for 4–8 fields per entity.
      One data field must be marked as the entity's primary key. Generate 3 rows of example data for each entity.
      Mark all mandatory fields in the entity with the "REQUIRED" tag. An entity name,
      along with the example data value, must each be at most 30 characters long.
    `
  })

  promptSections.push({
    title: 'Important Instructions',
    content: [
      'Explicit Field Inclusion:',
      `For every command that targets an aggregate root, include all the command arguments (input fields)
       in the corresponding entity. Even if a field appears only in one command
       (e.g., "Special Instructions" in the "Create Order" command), it must be included in the aggregate root.`,
      'Merge command Fields:',
      `When an aggregate root is involved in multiple commands, assemble all unique input arguments
       (data fields) from the associated commands. The resulting entity should have a field for every
       attribute mentioned in any of the commands.`,
      'Step-by-Step Directive:',
      '1. Identify the input data or arguments for each command.',
      '2. Determine which entity acts as the aggregate root for that command.',
      `3. Extract all unique arguments from these commands and include them in the
       aggregate root's definition, even if some arguments appear in only one command.`
    ]
  })

  const entities = [
    {
      name: 'Customer',
      dataFields: examples.entityExampleCustomer
    },
    {
      name: 'Product',
      dataFields: examples.entityExampleProduct
    }
  ]

  const entitiesObject = { entities }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(entities))
      : getJsonOutputFormatting('entities', JSON.stringify(entitiesObject))
  })

  promptSections.push({
    title: 'Note',
    contentType: 'bullet',
    content: [
      `The aggregate root is the main entity within the aggregate that the command is operating on and
       will typically have similar attributes as the Command plus an ID and sometimes more or less attributes`,
      'The associated entities are entities that are related to the aggregate root',
      `The associated entities are based on the attributes of the Command that are not
      part of the aggregate root but belong to other entities`,
      `For example, if the aggregate root is "Order" and one of the attributes on
       the order is "Customer" or "Customer Name", then an associated entity is likely "Customer"`
    ]
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
